<script>
    import { onMount } from "svelte";
    import CursorTrail from "$lib/components/CursorTrail.svelte";
    import CelestialSky from "$lib/components/CelestialSky.svelte";
    import { blogPosts, getBlogImage } from "$lib/data/blogPosts";

    const INTEROP_TAG = "Interop 2026";

    function hasInteropTag(post) {
        return post.tags.includes(INTEROP_TAG);
    }

    let searchQuery = "";
    let activeTag = "";
    let currentPage = 1;

    const POSTS_PER_PAGE = 24;
    const postOrderBySlug = new Map(
        blogPosts.map((post, index) => [post.slug, index]),
    );

    const allTags = [...new Set(blogPosts.flatMap((post) => post.tags))].sort(
        (left, right) => left.localeCompare(right),
    );

    const normalize = (value) => value.trim().toLowerCase();

    const isTagActive = (tag) => normalize(activeTag) === normalize(tag);

    const buildBlogFilterHref = (
        tag = activeTag,
        query = searchQuery,
        page = 1,
    ) => {
        const params = new URLSearchParams();
        const normalizedQuery = query.trim();
        const normalizedTag = tag.trim();

        if (normalizedQuery) {
            params.set("q", normalizedQuery);
        }

        if (normalizedTag) {
            params.set("tag", normalizedTag);
        }

        if (page > 1) {
            params.set("page", String(page));
        }

        const serialized = params.toString();
        return serialized ? `/blog?${serialized}` : "/blog";
    };

    const hydrateFiltersFromUrl = () => {
        if (typeof window === "undefined") {
            return;
        }

        const params = new URLSearchParams(window.location.search);
        searchQuery = params.get("q") ?? "";
        activeTag = params.get("tag") ?? "";
        const parsedPage = parseInt(params.get("page") ?? "1", 10);
        currentPage = isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;
    };

    const syncFiltersToUrl = (query = searchQuery, tag = activeTag) => {
        if (typeof window === "undefined") {
            return;
        }

        searchQuery = query;
        activeTag = tag;
        currentPage = 1;

        const nextHref = buildBlogFilterHref(activeTag, searchQuery, 1);
        const currentHref = `${window.location.pathname}${window.location.search}`;

        if (nextHref !== currentHref) {
            window.history.replaceState(window.history.state, "", nextHref);
        }
    };

    const handleSearchInput = (event) => {
        searchQuery = event.currentTarget.value;
        syncFiltersToUrl(searchQuery, activeTag);
    };

    const clearSearch = () => {
        if (!searchQuery) {
            return;
        }

        syncFiltersToUrl("", activeTag);
    };

    const BLOG_CARDS_SCROLL_TARGET_SELECTOR = "#blog-cards";

    const scrollToBlogPosts = () => {
        if (typeof window === "undefined") return;
        const target = document.querySelector(
            BLOG_CARDS_SCROLL_TARGET_SELECTOR,
        );
        if (!target) return;
        const top = window.scrollY + target.getBoundingClientRect().top - 24;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    };

    const toggleTagFilter = (tag) => {
        const nextTag = isTagActive(tag) ? "" : tag;
        syncFiltersToUrl(searchQuery, nextTag);
        scrollToBlogPosts();
    };

    const clearTagFilter = () => {
        if (!activeTag) {
            return;
        }

        syncFiltersToUrl(searchQuery, "");
    };

    const clearAllFilters = () => {
        syncFiltersToUrl("", "");
    };

    const comparePostsByRecency = (left, right) => {
        const dateDelta = right.date.localeCompare(left.date);

        if (dateDelta !== 0) {
            return dateDelta;
        }

        // Keep tie-breaking deterministic for same-day posts and prefer newest insertions.
        const leftOrder = postOrderBySlug.get(left.slug) ?? 0;
        const rightOrder = postOrderBySlug.get(right.slug) ?? 0;
        return rightOrder - leftOrder;
    };

    $: normalizedSearchQuery = normalize(searchQuery);
    $: normalizedActiveTag = normalize(activeTag);
    $: hasActiveFilters = Boolean(normalizedSearchQuery || normalizedActiveTag);

    $: filteredPosts = blogPosts
        .filter((post) => {
            const tagMatches =
                !normalizedActiveTag ||
                post.tags.some((tag) => normalize(tag) === normalizedActiveTag);

            if (!tagMatches) {
                return false;
            }

            if (!normalizedSearchQuery) {
                return true;
            }

            const searchIndex = [post.title, post.description, ...post.tags]
                .join(" ")
                .toLowerCase();

            return searchIndex.includes(normalizedSearchQuery);
        })
        .sort(comparePostsByRecency);

    $: filterSummary = hasActiveFilters
        ? [
              `Showing ${filteredPosts.length} filtered result${filteredPosts.length === 1 ? "" : "s"}.`,
              activeTag ? `Tag: "${activeTag}".` : "",
              searchQuery ? `Search: "${searchQuery}".` : "",
          ]
              .filter(Boolean)
              .join(" ")
        : "";

    $: totalPages = Math.max(
        1,
        Math.ceil(filteredPosts.length / POSTS_PER_PAGE),
    );

    $: if (currentPage > totalPages) {
        currentPage = totalPages;
    }

    $: if (currentPage < 1) {
        currentPage = 1;
    }

    $: pageStartIndex = (currentPage - 1) * POSTS_PER_PAGE;
    $: visiblePosts = filteredPosts.slice(
        pageStartIndex,
        pageStartIndex + POSTS_PER_PAGE,
    );

    const SEARCH_INPUT_SELECTOR = "#blog-search";
    const PAGINATION_SCROLL_TARGET_SELECTOR = SEARCH_INPUT_SELECTOR;
    const PAGINATION_SCROLL_OFFSET_PX = 24;
    const SEARCH_SCROLL_BLUR_THRESHOLD_PX = 10;
    let searchTouchStartX = 0;
    let searchTouchStartY = 0;

    const scrollToPaginationTarget = () => {
        if (typeof window === "undefined") {
            return;
        }

        const target = document.querySelector(
            PAGINATION_SCROLL_TARGET_SELECTOR,
        );

        if (!target) {
            return;
        }

        const targetTop =
            window.scrollY +
            target.getBoundingClientRect().top -
            PAGINATION_SCROLL_OFFSET_PX;

        window.scrollTo({
            top: Math.max(0, targetTop),
            behavior: "smooth",
        });
    };

    const handleSearchTouchStart = (event) => {
        if (event.touches.length === 0) {
            return null;
        }

        searchTouchStartX = event.touches[0].clientX;
        searchTouchStartY = event.touches[0].clientY;
    };

    const handleSearchTouchMove = (event) => {
        if (event.touches.length === 0) {
            return;
        }

        const input = event.currentTarget;

        if (!(input instanceof HTMLInputElement)) {
            return;
        }

        const deltaX = Math.abs(event.touches[0].clientX - searchTouchStartX);
        const deltaY = Math.abs(event.touches[0].clientY - searchTouchStartY);

        if (
            deltaY > SEARCH_SCROLL_BLUR_THRESHOLD_PX &&
            deltaY > deltaX &&
            document.activeElement === input
        ) {
            input.blur();
        }
    };

    const setPage = (pageNumber) => {
        if (pageNumber < 1 || pageNumber > totalPages) {
            return;
        }

        currentPage = pageNumber;

        if (typeof window !== "undefined") {
            const nextHref = buildBlogFilterHref(
                activeTag,
                searchQuery,
                pageNumber,
            );
            window.history.pushState(window.history.state, "", nextHref);

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    scrollToPaginationTarget();
                });
            });
        }
    };

    const goToPrevPage = () => setPage(currentPage - 1);
    const goToNextPage = () => setPage(currentPage + 1);

    // When totalPages > 7, use compact mode: [1] [current] [last]
    // The current-slot is hidden when currentPage === 1 or === totalPages.
    $: compactPagination = totalPages > 7;
    $: showCurrentSlot =
        compactPagination && currentPage !== 1 && currentPage !== totalPages;
    $: paginationItems = compactPagination
        ? []
        : Array.from({ length: totalPages }, (_, i) => i + 1);

    onMount(() => {
        hydrateFiltersFromUrl();

        const handlePopState = () => {
            hydrateFiltersFromUrl();
        };

        window.addEventListener("popstate", handlePopState);

        // Load GSAP dynamically
        const loadGSAP = () => {
            return new Promise((resolve, reject) => {
                if (window.gsap) {
                    resolve();
                    return;
                }

                const script = document.createElement("script");
                script.src =
                    "https://cdn.jsdelivr.net/npm/gsap@3.13.0/dist/gsap.min.js";
                script.onload = () => resolve();
                script.onerror = () => reject();
                document.head.appendChild(script);
            });
        };

        // Initialize cursor marquee effect after GSAP loads
        loadGSAP()
            .then(() => {
                initCursorMarqueeEffect();
            })
            .catch((err) => {
                console.warn("GSAP failed to load:", err);
            });

        function initCursorMarqueeEffect() {
            const hoverOutDelay = 0.4;
            const followDuration = 0.35;
            const speedMultiplier = 6;
            const cursor = document.querySelector(
                "[data-cursor-marquee-status]",
            );

            console.log("Cursor element found:", cursor);

            if (!cursor) {
                console.error("Cursor marquee element not found!");
                return;
            }

            const targets = cursor.querySelectorAll(
                "[data-cursor-marquee-text-target]",
            );

            console.log("Text targets found:", targets.length);

            const xTo = gsap.quickTo(cursor, "x", {
                duration: followDuration,
                ease: "power3",
            });
            const yTo = gsap.quickTo(cursor, "y", {
                duration: followDuration,
                ease: "power3",
            });

            let pauseTimeout = null;
            let activeEl = null;
            let lastX = 0;
            let lastY = 0;
            let rafId = null;
            const textCache = new Map();

            function getTextDuration(text) {
                if (textCache.has(text)) {
                    return textCache.get(text);
                }
                const duration = Math.max(1, text.length) / speedMultiplier;
                textCache.set(text, duration);
                return duration;
            }

            function getMarqueeTextForElement(el) {
                if (!el) return "";
                const baseText =
                    el.getAttribute("data-cursor-marquee-text") || "";

                if (!el.classList.contains("resource-card")) {
                    return baseText;
                }

                const iconText =
                    el.querySelector(".resource-icon")?.textContent?.trim() ||
                    "";

                if (!iconText || baseText.startsWith(iconText)) {
                    return baseText;
                }

                return `${iconText} ${baseText}`;
            }

            function playFor(el) {
                if (!el) return;
                if (pauseTimeout) {
                    clearTimeout(pauseTimeout);
                    pauseTimeout = null;
                }
                const text = getMarqueeTextForElement(el);
                const duration = getTextDuration(text);

                console.log("Playing cursor for:", text);

                requestAnimationFrame(() => {
                    targets.forEach((t) => {
                        t.textContent = text;
                        t.style.animationPlayState = "running";
                        t.style.animationDuration = duration + "s";
                    });
                    cursor.setAttribute("data-cursor-marquee-status", "active");
                });
                activeEl = el;
            }

            function pauseLater() {
                cursor.setAttribute("data-cursor-marquee-status", "not-active");
                if (pauseTimeout) clearTimeout(pauseTimeout);
                pauseTimeout = setTimeout(() => {
                    requestAnimationFrame(() => {
                        targets.forEach((t) => {
                            t.style.animationPlayState = "paused";
                        });
                    });
                }, hoverOutDelay * 1000);
                activeEl = null;
            }

            let checkScheduled = false;

            function checkTarget() {
                if (checkScheduled) return;
                checkScheduled = true;
                rafId = requestAnimationFrame(() => {
                    const el = document.elementFromPoint(lastX, lastY);
                    const hit = el && el.closest("[data-cursor-marquee-text]");
                    if (hit !== activeEl) {
                        if (activeEl) pauseLater();
                        if (hit) playFor(hit);
                    }
                    checkScheduled = false;
                });
            }

            const handleMove = (e) => {
                lastX = e.clientX;
                lastY = e.clientY;
                xTo(lastX);
                yTo(lastY);
                checkTarget();
            };

            const handleScroll = () => {
                xTo(lastX);
                yTo(lastY);
                checkTarget();
            };

            window.addEventListener("pointermove", handleMove, {
                passive: true,
            });
            window.addEventListener("scroll", handleScroll, { passive: true });

            setTimeout(() => {
                cursor.setAttribute("data-cursor-marquee-status", "not-active");
                console.log("Cursor marquee effect initialized successfully!");
            }, 100);

            return () => {
                window.removeEventListener("pointermove", handleMove);
                window.removeEventListener("scroll", handleScroll);
                if (rafId) cancelAnimationFrame(rafId);
                if (pauseTimeout) clearTimeout(pauseTimeout);
            };
        }

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    });
</script>

<svelte:head>
    <title>dinglandia | Blog - Web Development & Design Insights</title>
    <meta
        name="description"
        content="Insights, tutorials, and thoughts on web development, design, and performance optimization."
    />

    <meta property="og:title" content="dinglandia | Blog" />
    <meta property="og:site_name" content="dinglandia" />
    <meta property="og:url" content="https://dinglandia.design/blog" />
    <meta
        property="og:description"
        content="Insights, tutorials, and thoughts on web development, design, and performance optimization."
    />
    <meta property="og:type" content="website" />
    <meta property="og:image" content="https://dinglandia.design/og/blog.jpg" />
</svelte:head>

<!-- Custom Cursor Element -->
<div data-cursor-marquee-status="" class="cursor-marquee">
    <span class="cursor-marquee__dot" aria-hidden="true"></span>
    <div class="cursor-marquee__card">
        <span
            data-cursor-marquee-text-target=""
            class="cursor-marquee__text-span">Hello world</span
        >
        <span
            data-cursor-marquee-text-target=""
            class="cursor-marquee__text-span is--duplicate">Hello world</span
        >
    </div>
</div>

<!-- Intro -->
<section id="intro" class="intro" data-trail="wrapper">
    <div class="icon-new" name="logo"></div>
    <h1 class="flash">devblog</h1>

    <!-- Cursor Trail Images -->
    <CursorTrail />
</section>
<!-- //Intro -->

<!-- Down Arrow -->
<a href="#blog-posts" aria-label="Jump to blog posts">
    <svg class="icon icon-arrow down-arrow"
        ><use xlink:href="#icon-arrow"></use></svg
    >
</a>
<!-- //Down Arrow -->

<!-- Blog Posts Grid -->
<section id="blog-posts" class="blog-section">
    <div class="resources-section">
        <h2>Resources</h2>
        <div class="resources-grid">
            <a
                href="https://dinglandia.notion.site/TOOLS-1cb9a19a99754126a1e1a8f2ea40bf3c"
                target="_blank"
                rel="noopener noreferrer"
                class="resource-card"
                data-cursor-marquee-text="Open TOOLS Library"
            >
                <div class="resource-icon">🛠️</div>
                <span>TOOLS</span>
            </a>
            <a
                href="https://dinglandia.notion.site/9bbc7a19133c4c1ca1f0677b06d03ef4?v=b271149751734f03ba39ed164974dfb1"
                target="_blank"
                rel="noopener noreferrer"
                class="resource-card"
                data-cursor-marquee-text="Browse DEV Notes"
            >
                <div class="resource-icon">💻</div>
                <span>DEV</span>
            </a>
            <a
                href="https://dinglandia.notion.site/BYTES-6d04f9a0bf514829934dedc03c07e83d"
                target="_blank"
                rel="noopener noreferrer"
                class="resource-card"
                data-cursor-marquee-text="Read BYTES"
            >
                <div class="resource-icon resource-icon--code">&lt;/&gt;</div>
                <span>BYTES</span>
            </a>
            <a
                href="https://dinglandia.notion.site/INSPO-e1019830cc734349905d9bce74371f23"
                target="_blank"
                rel="noopener noreferrer"
                class="resource-card"
                data-cursor-marquee-text="Explore INSPO"
            >
                <div class="resource-icon">✨</div>
                <span>INSPO</span>
            </a>
            <a
                href="https://dinglandia.notion.site/PODCASTS-3093e6f13bdc802bb116f8acf86165f1"
                target="_blank"
                rel="noopener noreferrer"
                class="resource-card"
                data-cursor-marquee-text="Listen to PODCASTS"
            >
                <div class="resource-icon">🎙️</div>
                <span>PODCASTS</span>
            </a>
            <a
                href="https://arena.ai/leaderboard/code"
                target="_blank"
                rel="noopener noreferrer"
                class="resource-card"
                data-cursor-marquee-text="View AI Leaderboard"
            >
                <div class="resource-icon">🤖</div>
                <span>AI</span>
            </a>
        </div>
    </div>

    <div class="blog-intro">
        <h2>Articles</h2>
        <p>
            Explore practical insights on modern web development and design
            engineering. From performance optimization and accessibility best
            practices to cutting-edge CSS techniques and JavaScript frameworks,
            these articles cover the concepts and technologies that elevate the
            quality of digital experiences.
        </p>

        <div id="blog-filter-controls" class="blog-filters">
            <div class="filter-toolbar">
                <div class="filter-tags" aria-label="Filter by tag">
                    {#each allTags as tag}
                        <a
                            href={buildBlogFilterHref(tag, searchQuery)}
                            class="filter-tag"
                            class:is-active={isTagActive(tag)}
                            on:click|preventDefault={() => toggleTagFilter(tag)}
                        >
                            #{tag}
                        </a>
                    {/each}
                </div>

                <div class="filter-meta">
                    <p class="results-count">
                        {filteredPosts.length} article{filteredPosts.length ===
                        1
                            ? ""
                            : "s"} found
                    </p>
                    {#if activeTag}
                        <button
                            type="button"
                            class="active-tag-pill"
                            on:click={clearTagFilter}
                        >
                            Tag: {activeTag} ×
                        </button>
                    {/if}
                </div>
            </div>

            <label for="blog-search" class="search-label">Search articles</label
            >
            <div class="search-input-wrap">
                <input
                    id="blog-search"
                    name="blog-search"
                    class="search-input"
                    type="search"
                    placeholder="Search by title, description, or tag..."
                    value={searchQuery}
                    on:input={handleSearchInput}
                    on:touchstart|passive={handleSearchTouchStart}
                    on:touchmove|passive={handleSearchTouchMove}
                />
                {#if searchQuery}
                    <button
                        type="button"
                        class="search-clear"
                        on:click={clearSearch}
                        aria-label="Clear search"
                    >
                        Clear
                    </button>
                {/if}
            </div>
            {#if totalPages > 1}
                <p class="pagination-status" aria-live="polite">
                    Page {currentPage} of {totalPages}
                </p>
            {/if}
        </div>
    </div>

    <main id="blog-cards" class="blog-grid">
        {#if filteredPosts.length === 0}
            <article class="no-results">
                <h3>No matching posts</h3>
                <p>
                    Try a different keyword or clear the current tag filter to
                    see all articles.
                </p>
                <button
                    type="button"
                    class="reset-filters"
                    on:click={() => syncFiltersToUrl("", "")}
                >
                    Reset filters
                </button>
            </article>
        {:else}
            {#each visiblePosts as post}
                <div class="card-animation-layer">
                    <article
                        class="blog-card"
                        data-cursor-marquee-text={post.cursorText}
                    >
                        <a href="/posts/{post.slug}" class="card-link">
                            <img
                                src={getBlogImage(post.slug)}
                                width="600"
                                height="600"
                                alt="{post.title} thumbnail"
                                loading="lazy"
                            />
                            {#if hasInteropTag(post)}
                                <span class="interop-badge interop-badge--card"
                                    >Interop 2026</span
                                >
                            {/if}
                            <h2>{post.title}</h2>
                            <p>{post.description}</p>
                            <span class="read-more">Read more →</span>
                        </a>
                    </article>
                </div>
            {/each}
        {/if}
    </main>

    {#if hasActiveFilters}
        <div class="pagination-filter-note" aria-live="polite">
            <p>{filterSummary}</p>
            <button
                type="button"
                class="active-filters-clear"
                on:click={clearAllFilters}
            >
                Show all posts
            </button>
        </div>
    {/if}

    <!-- Pagination -->
    {#if totalPages > 1}
        <nav
            class="pagination"
            aria-label="Pagination navigation"
            data-ready="true"
        >
            <ul class="pagination__list" role="list">
                <!-- Prev arrow -->
                <li class="pagination__item pagination__item--arrow">
                    <button
                        type="button"
                        aria-label="Previous"
                        aria-disabled={currentPage === 1}
                        class="pagination__link pagination__link--arrow"
                        disabled={currentPage === 1}
                        on:click={goToPrevPage}
                    >
                        <svg
                            width="8"
                            height="12"
                            viewBox="0 0 8 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M7 11L2 6L7 1"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            ></path>
                        </svg>
                    </button>
                </li>

                {#if compactPagination}
                    <!-- Page 1 (always visible) -->
                    <li
                        class={`pagination__item ${currentPage === 1 ? "pagination__item--current" : ""}`}
                    >
                        <button
                            type="button"
                            aria-current={currentPage === 1
                                ? "page"
                                : undefined}
                            aria-label="Page 1"
                            class={`pagination__link ${currentPage === 1 ? "pagination__link--current" : ""}`}
                            on:click={() => setPage(1)}>1</button
                        >
                    </li>

                    <!-- Animated current-page slot (hidden when on page 1 or last) -->
                    {#if showCurrentSlot}
                        <li
                            class="pagination__item pagination__item--current pagination__item--animated"
                        >
                            {#key currentPage}
                                <button
                                    type="button"
                                    aria-current="page"
                                    aria-label={`Page ${currentPage}`}
                                    class="pagination__link pagination__link--current pagination__link--flip"
                                    on:click={() => setPage(currentPage)}
                                >
                                    {currentPage}
                                </button>
                            {/key}
                        </li>
                    {/if}

                    <!-- Last page (always visible) -->
                    <li
                        class={`pagination__item ${currentPage === totalPages ? "pagination__item--current" : ""}`}
                    >
                        <button
                            type="button"
                            aria-current={currentPage === totalPages
                                ? "page"
                                : undefined}
                            aria-label={`Page ${totalPages}`}
                            class={`pagination__link ${currentPage === totalPages ? "pagination__link--current" : ""}`}
                            on:click={() => setPage(totalPages)}
                            >{totalPages}</button
                        >
                    </li>
                {:else}
                    <!-- Standard mode: show all pages (≤7 total) -->
                    {#each paginationItems as item (item)}
                        <li
                            class={`pagination__item ${item === currentPage ? "pagination__item--current" : ""}`}
                        >
                            <button
                                type="button"
                                aria-current={item === currentPage
                                    ? "page"
                                    : undefined}
                                aria-label={`Page ${item}`}
                                class={`pagination__link ${item === currentPage ? "pagination__link--current" : ""}`}
                                on:click={() => setPage(item)}>{item}</button
                            >
                        </li>
                    {/each}
                {/if}

                <!-- Next arrow -->
                <li class="pagination__item pagination__item--arrow">
                    <button
                        type="button"
                        aria-label="Next"
                        aria-disabled={currentPage === totalPages}
                        class="pagination__link pagination__link--arrow"
                        disabled={currentPage === totalPages}
                        on:click={goToNextPage}
                    >
                        <svg
                            width="8"
                            height="12"
                            viewBox="0 0 8 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M1 1L6 6L1 11"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            ></path>
                        </svg>
                    </button>
                </li>
            </ul>
        </nav>
    {/if}
    <!-- //Pagination -->
</section>
<!-- //Blog Posts Grid -->

<!-- New Mountain Parallax -->
<main class="content-grid">
    <section class="breakout">
        <CelestialSky />
        <div
            class="frame frame-square parallax-y"
            role="img"
            aria-label="Multi-layered scene depicting an underwater scene of some fish and Manta Ray. Set within the outline of a Manta Ray."
            style="--multiplier: 4"
        >
            <img src="/img/mountains/mtnBlu5.svg" alt="" style="--offset:0.1" />
            <img
                src="/img/mountains/mtnBlu4.svg"
                alt=""
                style="--offset:0.075"
            />
            <img
                src="/img/mountains/mtnBlue3.svg"
                alt=""
                style="--offset:0.05"
            />
            <img
                src="/img/mountains/mtnBlu2_fix.svg"
                alt=""
                style="--offset:0.025"
            />
            <img
                src="/img/mountains/mtnBlue.svg"
                alt=""
                style="--offset:0.012"
            />
            <div class="mountain-base"></div>
        </div>
        <p class="supports"></p>
    </section>
</main>
<!-- //New Mountain Parallax -->

<!-- Services -->
<section id="services" class="services">
    <div class="container">
        <h2 class="text-center">What We Do</h2>
        <div class="services-grid">
            <a href="/services#v-pills-discover">
                <svg class="icon icon-discover"
                    ><use xlink:href="#icon-discover"></use></svg
                >
                <p>Discover</p>
            </a>
            <a href="/services#v-pills-design">
                <svg class="icon icon-design"
                    ><use xlink:href="#icon-design"></use></svg
                >
                <p>Design</p>
            </a>
            <a href="/services#v-pills-develop">
                <svg class="icon icon-develop"
                    ><use xlink:href="#icon-develop"></use></svg
                >
                <p>Develop</p>
            </a>
            <a href="/services#v-pills-devops">
                <svg class="icon icon-devops"
                    ><use xlink:href="#icon-devops"></use></svg
                >
                <p>DevOps</p>
            </a>
            <a href="/services#v-pills-agentic">
                <svg class="icon icon-robot"
                    ><use xlink:href="#icon-robot"></use></svg
                >
                <p>Agentic</p>
            </a>
        </div>

        <div class="row">
            <div class="col-md-6 offset-md-3">
                <div class="d-grid gap-2 col-6 mx-auto">
                    <a href="/work" class="btn btn-lg btn-custom" type="button"
                        >view work</a
                    >
                </div>
            </div>
        </div>
    </div>
</section>
<!-- //Services -->

<!-- Water Ripple CTA -->
<section class="ripple-cta">
    <div class="water-container">
        <canvas id="pool" aria-hidden="true"></canvas>
    </div>

    <div class="viking-ship-container">
        <svg
            class="viking-ship"
            viewBox="0 0 200 100"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Viking ship sailing"
        >
            <!-- Ship Hull -->
            <path
                d="M 20 50 Q 10 60, 20 70 L 180 70 Q 190 60, 180 50 Z"
                fill="#4a3426"
                stroke="#2a1f1a"
                stroke-width="2"
            />

            <!-- Hull Details -->
            <rect
                x="25"
                y="55"
                width="150"
                height="3"
                fill="#3a2920"
                opacity="0.7"
            />
            <rect
                x="25"
                y="62"
                width="150"
                height="2"
                fill="#3a2920"
                opacity="0.5"
            />

            <!-- Mast -->
            <rect x="98" y="15" width="4" height="45" fill="#2a1f1a" />

            <!-- Sail -->
            <path
                d="M 45 20 Q 100 25, 155 20 L 150 45 Q 100 48, 50 45 Z"
                fill="#e8d5c4"
                stroke="#2a1f1a"
                stroke-width="1.5"
                opacity="0.95"
            />

            <!-- Sail Stripes -->
            <rect
                x="50"
                y="25"
                width="100"
                height="4"
                fill="#b84c4c"
                opacity="0.8"
            />
            <rect
                x="50"
                y="33"
                width="100"
                height="4"
                fill="#b84c4c"
                opacity="0.8"
            />
            <rect
                x="50"
                y="41"
                width="100"
                height="3"
                fill="#b84c4c"
                opacity="0.8"
            />

            <!-- Shield decorations -->
            <circle
                cx="40"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="60"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="80"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="100"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="120"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="140"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="160"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />

            <!-- Dragon head -->
            <path
                d="M 180 50 Q 195 45, 197 50 L 195 55 Q 190 52, 180 52 Z"
                fill="#4a3426"
                stroke="#2a1f1a"
                stroke-width="1.5"
            />

            <!-- Oars (simplified) -->
            <line
                x1="35"
                y1="68"
                x2="28"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="55"
                y1="68"
                x2="48"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="75"
                y1="68"
                x2="68"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="125"
                y1="68"
                x2="132"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="145"
                y1="68"
                x2="152"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="165"
                y1="68"
                x2="172"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
        </svg>
    </div>

    <div class="trees trees--cta" aria-hidden="true"></div>

    <div class="overlay" aria-hidden="true">
        <div class="overlay-inner">
            <h2 class="title">We take you from point A to point B.</h2>
            <h3 class="subtitle">And everywhere in-between.</h3>
            <div class="cta">
                Let's create custom web experiences for your customers!
            </div>
            <div class="logo-tag">
                <button
                    type="button"
                    class="btn btn-lg btn-custom"
                    data-fillout-id="n2vCxyr9Huus"
                    data-fillout-embed-type="popup"
                    data-fillout-dynamic-resize
                    data-fillout-inherit-parameters
                    data-fillout-popup-size="small">navigate the winds</button
                >
            </div>
        </div>
    </div>

    <div class="fallback" hidden aria-hidden="true">
        WebGL2 is required to view this water effect.<br />
        Please try again in a modern browser.
    </div>
</section>

<!-- Water Ripple CTA -->

<style>
    .blog-section {
        position: relative;
        z-index: 1;
        background: var(--surface-1);
        padding-block: 5rem 10rem;
    }

    .resources-section {
        max-width: 1200px;
        margin: 0 auto;
        padding-inline: var(--size-5, 2rem);
        margin-block-end: var(--size-8, 4rem);
    }

    .resources-section h2 {
        font-size: var(--font-size-6, 2.5rem);
        color: var(--text);
        text-align: center;
        margin-block-end: var(--size-5, 2rem);
        font-weight: 700;
    }

    .resources-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
        gap: var(--size-4, 1.5rem);
        max-width: 1100px;
        margin: 0 auto;
    }

    .resource-card {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--size-2, 0.5rem);
        padding: var(--size-4, 1.5rem) var(--size-3, 1rem);
        background: light-dark(#232838, #ffffff);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-4, 16px);
        text-decoration: none;
        color: light-dark(#e0e0e0, #212121);
        transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
        box-shadow: var(--shadow-2, 0 4px 12px rgba(0, 0, 0, 0.2));
    }

    .resource-card:hover {
        transform: translateY(-4px);
        box-shadow: var(--shadow-4, 0 10px 30px rgba(0, 0, 0, 0.3));
    }

    .resource-icon {
        font-size: 2.5rem;
        line-height: 1;
    }

    .resource-icon--code {
        font-family: "Courier New", monospace;
        font-weight: bold;
        color: var(--link, #4a9eff);
    }

    .resource-card span {
        font-size: var(--font-size-2, 1rem);
        font-weight: 600;
        letter-spacing: 0.05em;
    }

    .blog-intro {
        max-width: 800px;
        margin: 0 auto;
        padding-inline: var(--size-5, 2rem);
        text-align: center;
    }

    .blog-intro h2 {
        font-size: var(--font-size-6, 2.5rem);
        color: var(--text);
        margin-block-end: var(--size-3, 1rem);
        font-weight: 700;
    }

    .blog-intro p {
        font-size: var(--font-size-2, 1rem);
        color: var(--text);
        line-height: 1.6;
        margin-block-end: var(--size-4, 1.5rem);
    }

    .blog-filters {
        display: grid;
        gap: 1rem;
        margin-block-start: 1rem;
    }

    .search-label {
        font-weight: 700;
        color: var(--text);
        text-align: left;
    }

    .search-input-wrap {
        position: relative;
    }

    .search-input {
        width: 100%;
        padding: 0.85rem 1rem;
        padding-inline-end: 5.75rem;
        border-radius: 0.8rem;
        border: 1px solid var(--border-color);
        background: var(--bg-surface);
        color: var(--text);
        font-size: 0.95rem;
        outline: none;
        appearance: none;
        -webkit-appearance: none;
        touch-action: pan-y;
        transition:
            border-color 0.2s ease,
            box-shadow 0.2s ease;
    }

    .search-input::-webkit-search-decoration,
    .search-input::-webkit-search-cancel-button,
    .search-input::-webkit-search-results-button,
    .search-input::-webkit-search-results-decoration {
        display: none;
        -webkit-appearance: none;
        appearance: none;
    }

    .search-input::-ms-clear,
    .search-input::-ms-reveal {
        display: none;
        width: 0;
        height: 0;
    }

    .search-input:focus-visible {
        border-color: rgba(29, 78, 216, 0.65);
        box-shadow: 0 0 0 4px rgba(29, 78, 216, 0.15);
    }

    .search-clear {
        position: absolute;
        inset-block-start: 50%;
        inset-inline-end: 0.5rem;
        transform: translateY(-50%);
        border: 0;
        border-radius: 999px;
        padding: 0.3rem 0.65rem;
        background: var(--border-color);
        color: var(--text);
        font-weight: 600;
        z-index: 1;
    }

    .pagination-status {
        margin: 4rem auto 0;
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--text-muted);
        text-align: center;
        letter-spacing: 0.01em;
    }

    .filter-toolbar {
        display: grid;
        gap: 0.8rem;
    }

    .filter-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        justify-content: center;
    }

    .filter-tag {
        text-decoration: none;
        color: light-dark(#1e3a8a, #93b4f5);
        border: 1px solid var(--border-color);
        background: var(--bg-surface);
        border-radius: 999px;
        padding: 0.35rem 0.7rem;
        font-size: 0.78rem;
        font-weight: 600;
        transition:
            background-color 0.2s ease,
            color 0.2s ease,
            border-color 0.2s ease;
    }

    .filter-tag.is-active {
        background: #1d4ed8;
        color: #ffffff;
        border-color: #1d4ed8;
    }

    .filter-meta {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 0.65rem;
    }

    .results-count {
        margin: 0;
        font-size: 0.9rem;
        color: var(--text-muted);
    }

    .active-tag-pill {
        border: 1px solid var(--border-color);
        background: var(--bg-elevated);
        color: light-dark(#1e3a8a, #93b4f5);
        border-radius: 999px;
        padding: 0.3rem 0.75rem;
        font-size: 0.8rem;
        font-weight: 700;
    }

    .pagination-filter-note {
        margin: 0 auto 0.8rem;
        max-width: 800px;
        padding: 0 2rem;
        display: flex;
        flex-wrap: wrap;
        gap: 0.6rem;
        justify-content: center;
        align-items: center;
        position: relative;
        z-index: 20;
    }

    .pagination-filter-note p {
        margin: 0;
        color: var(--text-muted);
        font-size: 0.86rem;
        text-align: center;
    }

    .active-filters-clear {
        border: 1px solid var(--border-color);
        background: var(--bg-surface);
        color: light-dark(#1e3a8a, #93b4f5);
        border-radius: 999px;
        padding: 0.35rem 0.8rem;
        font-size: 0.8rem;
        font-weight: 700;
        cursor: pointer;
    }

    .active-filters-clear:hover {
        border-color: var(--color-blue, #00b0ff);
        background: var(--bg-elevated);
        color: var(--color-blue, #00b0ff);
    }

    .blog-grid {
        --cols: 2;
        display: grid;
        grid-template-columns: repeat(var(--cols), minmax(0, 1fr));
        gap: var(--size-5, 2rem);
        padding-inline: var(--size-5, 2rem);
        padding-block: 2rem;
        place-items: start;
        max-width: 1920px;
        margin: 0 auto;
    }

    @media (width >= 720px) {
        .blog-grid {
            --cols: 4;
        }
    }
    @media (width >= 1200px) {
        .blog-grid {
            --cols: 6;
        }
    }
    @media (width >= 1920px) {
        .blog-grid {
            --cols: 8;
        }
    }

    /* Card Animation Layer */
    .card-animation-layer {
        display: grid;
        gap: var(--size-2, 0.5rem);
        height: 100%;
        min-width: 0;
    }

    /* Slide-in animation */
    @keyframes slide-in {
        from {
            scale: 0.85;
            rotate: calc((var(--side, 1) * (5deg * var(--amp, 1))));
        }
    }

    @media (prefers-reduced-motion: no-preference) {
        @supports (animation-timeline: view()) {
            .card-animation-layer {
                animation: slide-in linear both;
                animation-timeline: view();
                animation-range: cover 0% contain 15%;
            }
        }
    }

    /* Transform origins for different columns */
    .card-animation-layer:nth-of-type(2n + 1) {
        transform-origin: 25vw 100%;
        --side: -1;
    }
    .card-animation-layer:nth-of-type(2n) {
        transform-origin: -25vw 100%;
        --side: 1;
    }

    @media (width >= 720px) {
        .card-animation-layer:nth-of-type(4n + 1) {
            transform-origin: 50vw 100%;
            --side: -1;
            --amp: 2;
        }
        .card-animation-layer:nth-of-type(4n + 2) {
            transform-origin: 25vw 100%;
            --side: -1;
        }
        .card-animation-layer:nth-of-type(4n + 3) {
            transform-origin: -25vw 100%;
            --side: 1;
        }
        .card-animation-layer:nth-of-type(4n) {
            transform-origin: -50vw 100%;
            --side: 1;
            --amp: 2;
        }
    }

    @media (width >= 1200px) {
        .card-animation-layer:nth-of-type(6n + 1) {
            transform-origin: 75vw 100%;
            --side: -1;
            --amp: 3;
        }
        .card-animation-layer:nth-of-type(6n + 2) {
            transform-origin: 50vw 100%;
            --side: -1;
            --amp: 2;
        }
        .card-animation-layer:nth-of-type(6n + 3) {
            transform-origin: 25vw 100%;
            --side: -1;
        }
        .card-animation-layer:nth-of-type(6n + 4) {
            transform-origin: -25vw 100%;
            --side: 1;
        }
        .card-animation-layer:nth-of-type(6n + 5) {
            transform-origin: -50vw 100%;
            --side: 1;
            --amp: 2;
        }
        .card-animation-layer:nth-of-type(6n) {
            transform-origin: -75vw 100%;
            --side: 1;
            --amp: 3;
        }
    }

    @media (width >= 1920px) {
        .card-animation-layer:nth-of-type(8n + 1) {
            transform-origin: 100vw 100%;
            --side: -1;
            --amp: 4;
        }
        .card-animation-layer:nth-of-type(8n + 2) {
            transform-origin: 75vw 100%;
            --side: -1;
            --amp: 3;
        }
        .card-animation-layer:nth-of-type(8n + 3) {
            transform-origin: 50vw 100%;
            --side: -1;
            --amp: 2;
        }
        .card-animation-layer:nth-of-type(8n + 4) {
            transform-origin: 25vw 100%;
            --side: -1;
        }
        .card-animation-layer:nth-of-type(8n + 5) {
            transform-origin: -25vw 100%;
            --side: 1;
        }
        .card-animation-layer:nth-of-type(8n + 6) {
            transform-origin: -50vw 100%;
            --side: 1;
            --amp: 2;
        }
        .card-animation-layer:nth-of-type(8n + 7) {
            transform-origin: -75vw 100%;
            --side: 1;
            --amp: 3;
        }
        .card-animation-layer:nth-of-type(8n) {
            transform-origin: -100vw 100%;
            --side: 1;
            --amp: 4;
        }
    }

    /* Blog Card Styles */
    .blog-card {
        background: light-dark(#232838, #ffffff);
        border: 1px solid var(--border-color);
        padding-block: var(--size-3, 1rem) var(--size-2, 0.5rem);
        padding-inline: var(--size-3, 1rem);
        border-radius: var(--radius-4, 16px);
        box-shadow: var(--box-shadow);
        transition: transform 0.2s ease;
        height: 100%;
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    .blog-card:hover {
        transform: translateY(-4px);
    }

    .card-link {
        text-decoration: none;
        color: inherit;
        display: flex;
        flex-direction: column;
        height: auto;
        flex: 1;
        min-width: 0;
    }

    .interop-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        width: fit-content;
        padding: 0.22rem 0.55rem;
        border-radius: 999px;
        border: 1px solid color-mix(in srgb, #00b0ff 45%, transparent);
        background: linear-gradient(
            135deg,
            rgb(0 176 255 / 0.18),
            rgb(34 211 238 / 0.1)
        );
        color: light-dark(#dff7ff, #0b5a77);
        font-size: 0.64rem;
        font-weight: 800;
        letter-spacing: 0.025em;
        text-transform: uppercase;
        margin-block-end: 0.4rem;
    }

    .interop-badge::before {
        content: "";
        inline-size: 0.38rem;
        block-size: 0.38rem;
        border-radius: 999px;
        background: #22d3ee;
        box-shadow: 0 0 12px rgb(34 211 238 / 0.55);
    }

    .interop-badge--card {
        align-self: flex-start;
    }

    .blog-card h2 {
        font-size: var(--font-size-3, 1.25rem);
        line-height: 1.3;
        margin-block: var(--size-2, 0.5rem);
        color: light-dark(#e0e0e0, #212121);
        max-width: 100%;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow-wrap: anywhere;
        word-break: break-word;
    }

    .blog-card p {
        color: light-dark(#9ca3af, #666666);
        font-size: var(--font-size-1, 0.875rem);
        line-height: 1.5;
        margin-block-end: var(--size-2, 0.5rem);
        max-width: 100%;
        min-width: 0;
        display: -webkit-box;
        line-clamp: 3;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        overflow-wrap: anywhere;
        word-break: break-word;
        flex: 1;
    }

    .blog-card img {
        width: 100%;
        height: auto;
        background-color: var(--bg-elevated);
        border-radius: var(--radius-3, 12px);
        margin-block-end: var(--size-2, 0.5rem);
        clip-path: inset(0% 0% 0% 0% round 0px);
        transition:
            scale 0.2s ease,
            clip-path 0.2s ease;
    }

    .blog-card:hover img {
        scale: 1.2;
        clip-path: inset(10% 10% 10% 10% round 20px);
    }

    .read-more {
        color: var(--link, #4a9eff);
        font-size: var(--font-size-1, 0.875rem);
        font-weight: 600;
    }

    .no-results {
        grid-column: 1 / -1;
        max-width: 580px;
        margin: 0 auto;
        padding: 1.5rem;
        border-radius: 1rem;
        text-align: center;
        background: var(--bg-surface);
        border: 1px solid var(--border-color);
    }

    .no-results h3 {
        margin: 0;
        color: var(--text);
        font-size: 1.3rem;
    }

    .no-results p {
        margin: 0.8rem 0 1.1rem;
        color: var(--text-muted);
    }

    .reset-filters {
        border: 0;
        border-radius: 0.75rem;
        padding: 0.65rem 0.95rem;
        font-weight: 700;
        background: var(--link, #4a9eff);
        color: #ffffff;
    }

    /* Fallback for browsers without container queries */
    * {
        box-sizing: border-box;
    }

    /* Cursor Marquee Styles */
    :global(.blog-section) {
        cursor: auto;
    }

    :global([data-cursor-marquee-text]:is(:hover, :focus-visible)) {
        cursor: none !important;
    }

    :global([data-cursor-marquee-text]:is(:hover, :focus-visible) *) {
        cursor: none !important;
    }

    :global(.cursor-marquee) {
        z-index: 99999;
        pointer-events: none;
        position: fixed;
        display: flex;
        justify-content: center;
        align-items: center;
        transform: translate(-50%, -50%);
        will-change: transform;
        contain: layout style;
        top: 0;
        left: 0;
    }

    :global(.cursor-marquee__card) {
        color: var(--text);
        background-color: var(--bg-surface);
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        overflow: hidden;
        transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
        transform: translateY(0%) rotate(0.001deg);
        clip-path: inset(calc(50% - 0.25em) round 50em);
        will-change: clip-path, transform;
        opacity: 0;
        backdrop-filter: blur(10px);
        box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.3);
        min-width: 130px;
        min-height: 62px;
        padding: 0.65rem 1.1rem;
    }

    :global(.cursor-marquee__dot) {
        width: 14px;
        height: 14px;
        border-radius: 999px;
        background: #ffffff;
        position: absolute;
        transform: translateY(16px);
        opacity: 0;
        mix-blend-mode: difference;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.35);
        transition: opacity 0.15s ease-in-out;
        z-index: 2;
    }

    :global([data-cursor-marquee-status="active"] .cursor-marquee__card) {
        clip-path: inset(0 round 50em);
        transform: translateY(-25%) rotate(0.001deg);
    }

    :global([data-cursor-marquee-status="active"] .cursor-marquee__card) {
        opacity: 1;
    }

    :global([data-cursor-marquee-status="active"] .cursor-marquee__dot) {
        opacity: 1;
    }

    @keyframes translateXCursor {
        to {
            transform: translateX(-100%);
        }
    }

    :global(.cursor-marquee__text-span) {
        color: inherit;
        white-space: nowrap;
        transform-origin: 0;
        padding: 0.5em 0.75em;
        font-size: clamp(1.25rem, 1.15rem + 0.75vw, 1.75rem);
        font-weight: 600;
        line-height: 1;
        display: block;
        position: relative;
        animation: translateXCursor 10s linear infinite paused;
        transition: opacity 0.15s ease-in-out 0.25s;
        opacity: 0;
        will-change: transform;
    }

    :global([data-cursor-marquee-status="active"] .cursor-marquee__text-span) {
        transition: opacity 0.15s ease-in-out 0s;
        opacity: 1;
    }

    :global(.cursor-marquee__text-span.is--duplicate) {
        position: absolute;
        left: 100%;
    }

    /* Disable cursor effect on touch devices */
    @media (hover: none) and (pointer: coarse) {
        :global(body) {
            cursor: auto;
        }

        :global(.cursor-marquee) {
            display: none;
        }
    }

    /* Respect reduced motion preference */
    @media (prefers-reduced-motion: reduce) {
        :global(.cursor-marquee__text-span) {
            animation-duration: 0.01ms !important;
        }

        :global(.cursor-marquee__card) {
            transition-duration: 0.01ms !important;
        }
    }

    /* Pagination Styles */
    :global(.pagination) {
        --size: 36px;
        --inset: 2px;
        --pagination-radius: 4;
        --pagination-surface: light-dark(#1f2937, #ffffff);
        --pagination-surface-hover: light-dark(#111827, #f5f5f5);
        --pagination-border: light-dark(
            rgba(17, 24, 39, 0.78),
            rgba(17, 24, 39, 0.24)
        );
        --pagination-border-hover: light-dark(
            rgba(17, 24, 39, 0.98),
            rgba(17, 24, 39, 0.45)
        );
        --pagination-text: light-dark(#f8fafc, #212121);
        position: relative;
        z-index: 20;
        padding: 3rem 0;
        display: flex;
        justify-content: center;
    }

    :global(.pagination__list) {
        display: flex;
        list-style: none;
        align-items: center;
        padding: 0;
        margin: 0;
        position: relative;
    }

    :global(.pagination__list::before),
    :global(.pagination__list::after) {
        content: "";
        z-index: -1;
        position: absolute;
        width: calc(var(--size) - (2 * var(--inset)));
        aspect-ratio: 1;
        pointer-events: none;
        opacity: 0;
        border-radius: calc(var(--pagination-radius) * 1px);
    }

    :global(.pagination__list::before) {
        background: color-mix(
            in srgb,
            var(--text-1, currentColor) 16%,
            transparent
        );
    }

    :global(.pagination__list::after) {
        background: var(--color-yellow, #ffab00);
        opacity: 0;
        box-shadow: 0 8px 18px rgba(255, 171, 0, 0.35);
    }

    @supports (anchor-name: --pagination-active) {
        :global(.pagination__list::before) {
            position-anchor: --pagination-hover;
            top: calc(anchor(top) + var(--inset));
            left: calc((anchor(left)) + var(--inset));
        }

        :global(.pagination__list::after) {
            position-anchor: --pagination-active;
            top: calc(anchor(top) + var(--inset));
            left: calc((anchor(left)) + var(--inset));
            opacity: 1;
        }

        :global(
            .pagination__list:is(
                    :has(.pagination__link:hover),
                    :has(:focus-visible)
                ):not(
                    :has(.pagination__link--arrow:is(:hover, :focus-visible))
                )::before
        ) {
            opacity: 1;
        }

        :global([aria-current="page"]:not(.pagination__link--arrow)) {
            anchor-name: --pagination-active;
        }

        :global(
            li:has(
                .pagination__link:not(.pagination__link--arrow):is(
                        :hover,
                        :focus-visible
                    )
            )
        ) {
            anchor-name: --pagination-hover;
        }
    }

    @media (prefers-reduced-motion: no-preference) {
        :global(.pagination__list::before),
        :global(.pagination__list::after) {
            transition: inset 0.2s ease-out;
        }
    }

    :global(.pagination__item) {
        width: var(--size);
        aspect-ratio: 1;
        display: grid;
        place-items: center;
    }

    :global(.pagination__link) {
        display: grid;
        place-items: center;
        text-decoration: none;
        width: 100%;
        height: 100%;
        user-select: none;
        position: relative;
        outline-color: currentColor;
        -webkit-tap-highlight-color: #0000;
        border: 0;
        border-radius: calc(var(--pagination-radius) * 1px);
        background: transparent;
        font-weight: 700;
        font-size: 0.92rem;
        line-height: 1;
        color: var(--pagination-text);
        cursor: pointer;
    }

    @media (prefers-reduced-motion: no-preference) {
        :global(.pagination__link) {
            transition:
                color 0.2s ease-out,
                opacity 0.2s ease-out;
        }
    }

    :global(.pagination__link[aria-current="page"]) {
        color: #000;
        text-shadow: none;
    }

    :global(.pagination__link--current) {
        background: var(--color-yellow, #ffab00);
        border-color: var(--color-yellow, #ffab00);
        color: #000;
        box-shadow: 0 8px 18px rgba(255, 171, 0, 0.35);
    }

    :global(.pagination__link--current:hover) {
        background: var(--color-yellow, #ffab00);
        border-color: var(--color-yellow, #ffab00);
        color: #000;
    }

    /* Animated current-page slot */
    :global(.pagination__item--animated) {
        overflow: hidden;
    }

    @keyframes -global-page-flip-in {
        from {
            opacity: 0;
            transform: translateY(60%) scale(0.7);
        }
        to {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }

    @media (prefers-reduced-motion: no-preference) {
        :global(.pagination__link--flip) {
            animation: page-flip-in 0.22s cubic-bezier(0.34, 1.56, 0.64, 1) both;
        }
    }

    :global(.pagination__link) {
        color: var(--pagination-text);
    }

    :global(.pagination__link:not(.pagination__link--arrow)) {
        background: var(--pagination-surface);
        border: 1px solid var(--pagination-border);
    }

    :global(.pagination__link:not(.pagination__link--arrow):hover) {
        color: var(--pagination-text);
        border-color: var(--pagination-border-hover);
        background: var(--pagination-surface-hover);
    }

    :global(.pagination__link--arrow) {
        border: 1px solid var(--pagination-border);
        background: var(--pagination-surface);
        color: var(--pagination-text);
        opacity: 1;
    }

    :global(.pagination__link--arrow svg) {
        width: 7px;
        height: 10px;
    }

    :global(.pagination__link--arrow:hover:not(:disabled)) {
        border-color: var(--pagination-border-hover);
        background: var(--pagination-surface-hover);
    }

    /* Active page: yellow to pop against both light and dark surfaces. */
    :global(.pagination__link--current),
    :global(.pagination__link[aria-current="page"]) {
        color: #000;
        background: var(--color-yellow, #ffab00);
        border-color: var(--color-yellow, #ffab00);
        text-shadow: none;
        box-shadow: 0 4px 12px rgba(255, 171, 0, 0.3);
    }

    :global(.pagination__link--current:hover) {
        color: #000;
        background: var(--color-yellow, #ffab00);
        border-color: var(--color-yellow, #ffab00);
    }

    :global(.pagination__link--arrow:disabled) {
        opacity: 0.35;
        cursor: not-allowed;
        pointer-events: none;
    }

    :global(.pagination__link--gap) {
        cursor: default;
        pointer-events: none;
    }

    :global(.pagination__gap) {
        cursor: default;
    }

    @media (max-width: 640px) {
        :global(.pagination__item--mobile-only) {
            display: none;
        }

        :global(
            .pagination__item:not(
                .pagination__item--arrow,
                .pagination__item--current
            )
        ) {
            display: none;
        }
    }
</style>
